// UI sound effects, synthesised with the Web Audio API — no audio assets.
// Character chosen from design/sound_variants.html (2026-09-05):
//   theme toggle  → whoosh (band-passed noise sweep; falling into light, rising into dark)
//   switch        → felt thud (low-passed noise puff + ~90 Hz sine; higher for "on")
//   click         → between mechanical click and felt thud (short transient over a soft thump)
//   slider        → between soft pop, bubble and analog blip (low-passed triangle, slight rise)
// Master level and on/off come from prefs; the AudioContext is created lazily
// on the first user gesture, which is what browsers' autoplay policy wants.

import { getPref } from "@/prefs";

let ctx: AudioContext | null = null;
let noiseBuf: AudioBuffer | null = null;

function context(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    try {
      ctx = new AudioContext();
    } catch {
      return null;
    }
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function noise(ac: AudioContext): AudioBuffer {
  if (noiseBuf) return noiseBuf;
  const b = ac.createBuffer(1, ac.sampleRate, ac.sampleRate);
  const d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  noiseBuf = b;
  return b;
}

/** Master gain from prefs, or null when sounds are off / muted. */
function master(force: boolean): { ac: AudioContext; out: GainNode; t: number } | null {
  if (!force && !getPref("sounds")) return null;
  const level = Math.max(0, Math.min(1, getPref("volume")));
  if (level === 0) return null;
  const ac = context();
  if (!ac) return null;
  const out = ac.createGain();
  out.gain.value = level;
  out.connect(ac.destination);
  return { ac, out, t: ac.currentTime + 0.005 };
}

function envelope(g: GainNode, t: number, peak: number, attack: number, dur: number, linear = false) {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(peak, t + attack);
  if (linear) g.gain.linearRampToValueAtTime(0.0001, t + dur);
  else g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
}

interface ToneOpts {
  freq: number;
  dur: number;
  gain: number;
  type?: OscillatorType;
  sweepTo?: number;
  attack?: number;
  lowpass?: number;
}
function tone(ac: AudioContext, out: AudioNode, t: number, o: ToneOpts) {
  const osc = ac.createOscillator();
  osc.type = o.type ?? "sine";
  osc.frequency.setValueAtTime(o.freq, t);
  if (o.sweepTo) osc.frequency.exponentialRampToValueAtTime(o.sweepTo, t + o.dur);
  const g = ac.createGain();
  envelope(g, t, o.gain, o.attack ?? 0.004, o.dur);
  let head: AudioNode = osc;
  if (o.lowpass) {
    const lp = ac.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = o.lowpass;
    head.connect(lp);
    head = lp;
  }
  head.connect(g).connect(out);
  osc.start(t);
  osc.stop(t + o.dur + 0.05);
}

interface NoiseOpts {
  dur: number;
  gain: number;
  type: BiquadFilterType;
  freq: number;
  q?: number;
  sweepTo?: number;
  attack?: number;
  linear?: boolean;
}
function burst(ac: AudioContext, out: AudioNode, t: number, o: NoiseOpts) {
  const src = ac.createBufferSource();
  src.buffer = noise(ac);
  const f = ac.createBiquadFilter();
  f.type = o.type;
  f.frequency.setValueAtTime(o.freq, t);
  f.Q.value = o.q ?? 1;
  if (o.sweepTo) f.frequency.exponentialRampToValueAtTime(o.sweepTo, t + o.dur);
  const g = ac.createGain();
  envelope(g, t, o.gain, o.attack ?? 0.002, o.dur, o.linear);
  src.connect(f).connect(g).connect(out);
  src.start(t);
  src.stop(t + o.dur + 0.05);
}

/** Theme toggle — whoosh: a band-pass sweep falling into light, rising into dark. */
export function playThemeChime(toLight: boolean): void {
  const m = master(false);
  if (!m) return;
  burst(m.ac, m.out, m.t, {
    dur: 0.32,
    gain: 0.36,
    type: "bandpass",
    freq: toLight ? 1100 : 240,
    sweepTo: toLight ? 240 : 1100,
    q: 2.2,
    attack: 0.07,
    linear: true,
  });
}

/** Settings switch — felt thud: soft puff plus a low sine, a touch higher for "on". */
export function playSwitch(on: boolean, opts: { force?: boolean } = {}): void {
  const m = master(opts.force ?? false);
  if (!m) return;
  const f = on ? 110 : 85;
  burst(m.ac, m.out, m.t, { dur: 0.05, gain: 0.3, type: "lowpass", freq: 700 });
  tone(m.ac, m.out, m.t, { freq: f, sweepTo: f * 0.7, dur: 0.09, gain: 0.5 });
}

/** Plain button click — a mechanical transient sitting on a felt thump. */
export function playClick(): void {
  const m = master(false);
  if (!m) return;
  burst(m.ac, m.out, m.t, { dur: 0.012, gain: 0.35, type: "highpass", freq: 2200 });
  burst(m.ac, m.out, m.t, { dur: 0.04, gain: 0.18, type: "lowpass", freq: 800 });
  tone(m.ac, m.out, m.t, { freq: 120, sweepTo: 85, dur: 0.07, gain: 0.32 });
}

/** Slider release — low-passed triangle blip with a slight rise; pitch follows the value 0..1. */
export function playSliderTick(value = 0.5): void {
  const m = master(false);
  if (!m) return;
  const f = 260 + 260 * value;
  tone(m.ac, m.out, m.t, {
    freq: f,
    sweepTo: f * 1.35,
    dur: 0.06,
    gain: 0.22,
    type: "triangle",
    lowpass: 1400,
    attack: 0.008,
  });
}
